import type { ApiUserAccount, ApiChangeEmailConfirmPayload } from 'src/types/userAuth';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';

/**
 * POST /user/auth/email/change/confirm — verifies both codes (the one mailed to the new address and
 * the one mailed to the current one) plus, for an account with a linked wallet, a signature by that
 * wallet, and moves the account email. Sessions are keyed by account id and are NOT rotated, so the
 * existing user token keeps working. On success the returned account is written into the `userMe`
 * cache so the Profile shows the new email immediately (no extra round-trip, no flicker).
 */
export const useConfirmChangeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ApiChangeEmailConfirmPayload): Promise<ApiUserAccount> => {
      const response = await axios.post<ApiUserAccount>(`/user/auth/email/change/confirm`, payload);
      return response.data;
    },
    onSuccess: (account) => {
      queryClient.setQueriesData({ queryKey: QueryKeys.userMe() }, account);
    },
  });
};
